import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import InputField from "../../Common/InputField";
import ToggleSwitch from "../../Common/ToggleSwitch";
import FormCard from "../../Common/FormCard";
import {
  sanitizeLabName,
  normalizeSpaces,
  Button,
  CancelButton,
  hasPrivilege,
  PRIVILEGES,
} from "../../Common/UIComponents";
import {
  fetchEmployeeById,
  createUpdateEmployee,
  fetchEmployees,
  checkEmployeeCodeExists,
  checkUsernameExists,
  checkMobileNumberExists,
} from "../../API_Service/Employee_Slice";


import { fetchLocations as fetchLabLocationList } from "../../API_Service/LabBooking_Slice";
import CreateEditRolePage from "../RoleManagement/CreateEditRolePage";


const QuickAddRoleModal = ({ isOpen, onClose, onCreated }) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[210] overflow-y-auto">
        <div className="min-h-full flex items-start justify-center p-4">
          <div
            className="bg-[#f7fafa] rounded-2xl shadow-2xl w-full max-w-6xl my-4 overflow-hidden relative"
            style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
          >
            <style>{`@keyframes fadeUp{from:{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

            {/* Close button — floats above the embedded page */}
            <button
              onClick={onClose}
              title="Close"
              className="absolute top-4 right-4 z-10 h-9 w-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-[#08757C] hover:border-[#08757C]/40 transition-all"
            >
              <span className="material-symbols-outlined text-[18px]">
                close
              </span>
            </button>

            <CreateEditRolePage
              embedded
              onCancel={onClose}
              onSuccess={(newId, newName) => {
                onCreated?.(newId, newName);
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const ASSOCIATE_NAME_MAX = 100;
const USERNAME_MAX = 50;
const PASSWORD_MAX = 500;
const EMAIL_MAX = 100;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div
      className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}
    >
      <span>
        {len}/{max}
      </span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

const CreateEditUserPage = ({ embedded = false, onCancel, onSuccess } = {}) => {
  const handleEmbeddedCancel = () =>
    embedded ? onCancel?.() : navigate("/user-management");
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.CompanyId || localStorage.getItem("companyId");
  const employeeId = user?.EmployeeID || localStorage.getItem("empid");
  const { selectedEmployee } = useSelector((state) => state.employee);

  const [formData, setFormData] = useState({
    associateCode: "",
    firstName: "",
    phoneCode: "+91",
    phoneNumber: "",
    email: "",
    username: "",
    password: "",
    roleIds: [],
    // Which cities/locations an LM can access — only shown/relevant once
    // the LM role is checked below.
    cityIds: [],
    isActive: true,
  });

  const [roleOptions, setRoleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);


  useEffect(() => {
    if (!companyId) return;

    dispatch(fetchLabLocationList({ companyID: companyId, empID: 0 }))
      .then((res) => {
        const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
        const rows = Array.isArray(d) ? d : [];
        setCityOptions(
          rows.map((r) => ({
            value: r.cityID,
            label: r.cityName ?? `Location ${r.cityID}`,
          })),
        );
      })
      .catch(() => {});
  }, [companyId, dispatch]);

  
  const locationListRef = useRef(null);
  const pendingLocationScrollRef = useRef(false);
  useEffect(() => {
    if (!pendingLocationScrollRef.current) return;
    if (cityOptions.length === 0 || formData.cityIds.length === 0) return;
    const container = locationListRef.current;
    if (!container) return;
    const firstSelected = container.querySelector(
      `[data-city-id="${formData.cityIds[0]}"]`,
    );
    firstSelected?.scrollIntoView({ block: "nearest" });
    pendingLocationScrollRef.current = false;
  }, [cityOptions, formData.cityIds]);


  const isLmSelected = roleOptions.some(
    (role) =>
      formData.roleIds.includes(role.value) &&
      role.code?.trim().toUpperCase() === "LM",
  );

  const handleCityChange = (value, checked) => {
    setFormData((prev) => ({
      ...prev,
      cityIds: checked
        ? [...prev.cityIds, value]
        : prev.cityIds.filter((cityId) => cityId !== value),
    }));
  };

  // Field validation errors
  const [errors, setErrors] = useState({
    associateCode: "",
    firstName: "",
    email: "",
    username: "",
    password: "",
    roleIds: "",
    phoneCode: "",
    phoneNumber: "",
  });

  // Quick role-create modal
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);

  // Validation states for async checks
  const [associateCodeError, setAssociateCodeError] = useState("");
  const [isCheckingAssociateCode, setIsCheckingAssociateCode] = useState(false);
  const [associateCodeValid, setAssociateCodeValid] = useState(false);

  const [usernameError, setUsernameError] = useState("");
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameValid, setUsernameValid] = useState(false);

  const [mobileError, setMobileError] = useState("");
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);
  const [mobileValid, setMobileValid] = useState(false);

  const [successMsg, setSuccessMsg] = useState("");

  const isEditMode = id && id !== "create" && parseInt(id) > 0;
  const isInactiveEdit = isEditMode && !formData.isActive;

  useEffect(() => {
    if (!id || id === "create") {
      setFormData({
        associateCode: "",
        firstName: "",
        phoneCode: "+91",
        phoneNumber: "",
        email: "",
        username: "",
        password: "",
        roleIds: [],
        cityIds: [],
        isActive: true,
      });
      setRoleOptions([]);
      setErrors({
        associateCode: "",
        firstName: "",
        email: "",
        username: "",
        password: "",
        roleIds: "",
        phoneCode: "",
        phoneNumber: "",
      });
    }
  }, [id]);

  // Check if Associate Code exists
  const checkAssociateCodeExists = async (code) => {
    if (!code || code.trim() === "") {
      setAssociateCodeError("");
      setAssociateCodeValid(false);
      return;
    }

    if (id && id !== "create" && selectedEmployee?.EmpCode === code) {
      setAssociateCodeError("");
      setAssociateCodeValid(true);
      return;
    }

    setIsCheckingAssociateCode(true);
    try {
      const result = await dispatch(
        checkEmployeeCodeExists({
          code: code,
          companyId: companyId,
        }),
      ).unwrap();

      if (result.exists) {
        setAssociateCodeError(
          "Associate code already exists. Please enter a different code.",
        );
        setAssociateCodeValid(false);
        setErrors((prev) => ({
          ...prev,
          associateCode: "Associate code already exists",
        }));
      } else {
        setAssociateCodeError("");
        setAssociateCodeValid(true);
        setErrors((prev) => ({ ...prev, associateCode: "" }));
      }
    } catch (error) {
      console.error("Error checking associate code:", error);
      setAssociateCodeError(
        "Error checking code availability. Please try again.",
      );
      setAssociateCodeValid(false);
    } finally {
      setIsCheckingAssociateCode(false);
    }
  };

  // Check if Username exists
  const checkUsernameExistsAPI = async (username) => {
    if (!username || username.trim() === "") {
      setUsernameError("");
      setUsernameValid(false);
      return;
    }

    if (isEditMode && selectedEmployee?.UserName === username) {
      setUsernameError("");
      setUsernameValid(true);
      setErrors((prev) => ({ ...prev, username: "" }));
      return;
    }

    if (
      id &&
      id !== "create" &&
      selectedEmployee?.UserName?.split("@")[0] === username
    ) {
      setUsernameError("");
      setUsernameValid(true);
      setErrors((prev) => ({ ...prev, username: "" }));
      return;
    }

    setIsCheckingUsername(true);
    try {
      const result = await dispatch(
        checkUsernameExists({
          username: username,
        }),
      ).unwrap();

      if (result.exists) {
        setUsernameError(
          "Username already exists. Please enter a different username.",
        );
        setUsernameValid(false);
        setErrors((prev) => ({ ...prev, username: "Username already exists" }));
      } else {
        setUsernameError("");
        setUsernameValid(true);
        setErrors((prev) => ({ ...prev, username: "" }));
      }
    } catch (error) {
      console.error("Error checking username:", error);
      setUsernameError(
        "Error checking username availability. Please try again.",
      );
      setUsernameValid(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // Check if Mobile Number exists
  const checkMobileNumberExistsAPI = async (phoneNumber, phoneCode) => {
    const fullNumber = phoneCode + phoneNumber;
    if (!phoneNumber || phoneNumber.trim() === "") {
      setMobileError("");
      setMobileValid(false);
      return;
    }

    const originalFullNumber =
      selectedEmployee?.ContactCode + (selectedEmployee?.ContactNumber || "");
    if (isEditMode && originalFullNumber === fullNumber) {
      setMobileError("");
      setMobileValid(true);
      return;
    }

    setIsCheckingMobile(true);
    try {
      const result = await dispatch(
        checkMobileNumberExists({
          id: id && id !== "create" ? parseInt(id) : 0,
          number: fullNumber,
          companyId: companyId,
        }),
      ).unwrap();

      if (result.exists) {
        setMobileError(
          "Mobile number already exists. Please enter a different number.",
        );
        setMobileValid(false);
      } else {
        setMobileError("");
        setMobileValid(true);
      }
    } catch (error) {
      console.error("Error checking mobile number:", error);
      setMobileError(
        "Error checking mobile number availability. Please try again.",
      );
      setMobileValid(false);
    } finally {
      setIsCheckingMobile(false);
    }
  };

  // Handle role selection change (multi-select)
  const handleRoleChange = (value, checked) => {
    let updatedRoles = [...formData.roleIds];
    if (checked) {
      updatedRoles.push(value);
    } else {
      updatedRoles = updatedRoles.filter((roleId) => roleId !== value);
    }
    setFormData((prev) => ({ ...prev, roleIds: updatedRoles }));
    if (errors.roleIds) {
      setErrors((prev) => ({ ...prev, roleIds: "" }));
    }
  };

  // Country code → expected digit count (E.164 subscriber number length)
  const PHONE_LENGTH_MAP = {
    "+91": 10, // India
    "+1": 10, // USA / Canada
    "+44": 10, // UK
    "+61": 9, // Australia
    "+971": 9, // UAE
    "+65": 8, // Singapore
    "+60": 9, // Malaysia
    "+94": 9, // Sri Lanka
    "+92": 10, // Pakistan
    "+880": 10, // Bangladesh
    "+33": 9, // France
    "+49": 10, // Germany
    "+81": 10, // Japan
    "+86": 11, // China
  };

  const expectedPhoneLength = PHONE_LENGTH_MAP[formData.phoneCode] || null;

  // Validate required fields before save
  const validateForm = () => {
    const newErrors = {
      associateCode: "",
      firstName: "",
      email: "",
      username: "",
      password: "",
      roleIds: "",
      phoneCode: "",
      phoneNumber: "",
    };
    let isValid = true;

    if (!formData.associateCode.trim()) {
      newErrors.associateCode = "Associate code is required";
      isValid = false;
    }
    else if (formData.associateCode.length !== 8) {
  newErrors.associateCode = "Enter valid Employee Code";
  isValid = false;
} 
     else if (associateCodeError) {
      newErrors.associateCode = associateCodeError;
      isValid = false;
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^(?!.*\.\.)[a-zA-Z0-9][a-zA-Z0-9._%+\-]*@[a-zA-Z0-9][a-zA-Z0-9\-]*(\.[a-zA-Z0-9\-]+)*\.[a-zA-Z]{2,}$/.test(
        formData.email,
      )
    ) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
      isValid = false;
    } else if (usernameError) {
      newErrors.username = usernameError;
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    if (formData.roleIds.length === 0) {
      newErrors.roleIds = "Please select at least one role";
      isValid = false;
    }

    if (!formData.phoneCode.trim()) {
      newErrors.phoneCode = "Country code is required";
      isValid = false;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
      isValid = false;
    } else if (
      expectedPhoneLength &&
      formData.phoneNumber.length !== expectedPhoneLength
    ) {
      newErrors.phoneNumber = `Phone number must be exactly ${expectedPhoneLength} digits for ${formData.phoneCode}`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Debounce functions
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.associateCode) {
        checkAssociateCodeExists(formData.associateCode);
      } else {
        setAssociateCodeError("");
        setAssociateCodeValid(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.associateCode]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username) {
        checkUsernameExistsAPI(formData.username);
      } else {
        setUsernameError("");
        setUsernameValid(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.phoneNumber) {
        checkMobileNumberExistsAPI(formData.phoneNumber, formData.phoneCode);
      } else {
        setMobileError("");
        setMobileValid(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.phoneNumber, formData.phoneCode]);

  // Fetch employee data
  useEffect(() => {
    const empID = id && id !== "create" ? parseInt(id) : 0;
    if (companyId) {
      dispatch(fetchEmployeeById({ empID, companyId: companyId }));
    }
  }, [id, dispatch, companyId]);

  // Set form data when selectedEmployee is loaded
  useEffect(() => {
    if (selectedEmployee) {
      // Set role options and selected roles from RoleList
      if (selectedEmployee.RoleList && selectedEmployee.RoleList.length > 0) {
        const options = selectedEmployee.RoleList.map((role) => ({
          value: role.Value,
          label: role.Text,
          selected: role.Selected,
         
          code: role.RoleCode ?? role.Role_Code ?? role.Code ?? role.roleCode ?? null,
        }));
        if (options.length > 0 && options.every((r) => r.code == null)) {
          console.warn(
            "[CreateEditUserPage] RoleList has no RoleCode/Role_Code/Code field — inspect a real response to find the right key.",
            selectedEmployee.RoleList[0],
          );
        }
        setRoleOptions(options);

        // Get selected role IDs
        const selectedRoleIds = options
          .filter((role) => role.selected)
          .map((role) => role.value);

        setFormData((prev) => ({ ...prev, roleIds: selectedRoleIds }));
      }

      
      if (selectedEmployee.LocationID) {
        const parsedCityIds = String(selectedEmployee.LocationID)
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .map(Number)
          .filter((n) => !Number.isNaN(n));
        setFormData((prev) => ({ ...prev, cityIds: parsedCityIds }));
        pendingLocationScrollRef.current = parsedCityIds.length > 0;
      }

      if (selectedEmployee.EmployeeID > 0) {
        let phoneCode = "+1";
        let phoneNumber = selectedEmployee.ContactNumber || "";

        const commonCodes = [
          "+1",
          "+44",
          "+91",
          "+61",
          "+49",
          "+33",
          "+81",
          "+86",
          "+55",
          "+27",
          "+971",
          "+966",
        ];
        const matchedCode = commonCodes.find((code) =>
          phoneNumber.startsWith(code),
        );
        if (matchedCode) {
          phoneCode = matchedCode;
          phoneNumber = phoneNumber.substring(matchedCode.length);
        }

        setFormData((prev) => ({
          ...prev,
          associateCode: normalizeSpaces(selectedEmployee.EmpCode || ""),
          firstName: normalizeSpaces(selectedEmployee.EmployeeName || ""),
          phoneCode: selectedEmployee.ContactCode || phoneCode,
          phoneNumber: phoneNumber,
          email: selectedEmployee.EmailID || "",
          username: selectedEmployee.UserName,
          password: selectedEmployee.Password || "",
          isActive: selectedEmployee.IsActive === 1,
        }));

        setAssociateCodeValid(true);
        setUsernameValid(true);
        setMobileValid(true);
      }
    }
  }, [selectedEmployee]);

  const handleInputChange = (value, e) => {
    const { name } = e.target;
    const sanitized =
      name === "associateCode"
        ? value.replace(/[^0-9]/g, "")
        : name === "firstName" || name === "associateCode"
          ? sanitizeLabName(value)
          : name === "password"
            ? value.replace(/^\s+/, "")
            : name === "username"
              ? value.replace(/\s/g, "")
              : value;
    setFormData((prev) => ({ ...prev, [name]: sanitized }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "password") {
      setErrors((prev) => ({ ...prev, password: "" }));
    }
    // Clear save error message when user modifies email or username
    if ((name === "email" || name === "username") && saveError) {
      setSaveError(null);
    }
  };

  const handleMobileNumberChange = (value) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, phoneNumber: numericValue }));
    // Clear any error messages when user modifies mobile number
    if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    if (mobileError) setMobileError("");
    setSaveError(null);
  };

  const handleSaveUser = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const isEdit = id && id !== "create" && parseInt(id) > 0;
      const employeeName = normalizeSpaces(formData.firstName);
      const roleIdsString = formData.roleIds.join(",");
      
      const cityIdsString = isLmSelected ? formData.cityIds.join(",") : "";

      const employeeData = {
        EmpCode: normalizeSpaces(formData.associateCode),
        EmployeeID: isEdit ? parseInt(id) : 0,
        EmployeeName: employeeName,
        ContactCode: formData.phoneCode,
        ContactNumber: formData.phoneNumber,
        EmailID: formData.email,
        UserName: formData.username,
        Password: formData.password,
        RoleID: roleIdsString,
        LocationID: cityIdsString,
        IsActive: formData.isActive ? 1 : 0,
        SavedBy: parseInt(employeeId) || 1,
        SavedDate: new Date().toISOString(),
      };

      const result = await dispatch(
        createUpdateEmployee({ employeeData, companyId: companyId }),
      ).unwrap();

      await dispatch(fetchEmployees(companyId));
      if (embedded) {
        onSuccess?.(result?.data?.EmployeeID, formData.firstName);
      } else {
        navigate("/user-management", {
          state: { refresh: true, editSuccess: true, isNew: !isEdit },
        });
      }
    } catch (error) {
      const msg = error?.message;
      if (msg === "EMAIL_ALREADY_EXISTS") {
        setSaveError("This email address is already registered...");
        setErrors((prev) => ({ ...prev, email: "Email already exists" }));
      } else if (msg === "PHONE_ALREADY_EXISTS") {
        setSaveError("This mobile number is already registered...");
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Mobile number already exists",
        }));
      } else if (msg === "EMPLOYEE_MAPPED_WITH_SHIFT") {
        setSaveError("This associate is mapped to a shift...");
      } else {
        setSaveError("Failed to save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const Wrapper = embedded ? React.Fragment : DashboardLayout;

  return (
    <Wrapper>
      <div className="px-8 py-5 flex-1 w-full">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 gap-4">
          <div>
            {!embedded && (
              <nav className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                <span
                  onClick={handleEmbeddedCancel}
                  className="cursor-pointer hover:text-primary transition-colors"
                >
                  User Management
                </span>
                <span className="material-symbols-outlined text-[12px]">
                  chevron_right
                </span>
                <span className="text-primary">Registration</span>
              </nav>
            )}
            <h2 className="text-3xl font-extrabold text-primary tracking-tight">
              {id && id !== "create" && parseInt(id) > 0
                ? "Edit Associate"
                : "Create New Associate"}
            </h2>
          </div>
          <div className={`flex items-center gap-3 ${embedded ? "mr-12" : ""}`}>
            {/* {!embedded && (
              <button
                onClick={handleEmbeddedCancel}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-slate-600 font-bold text-sm hover:bg-slate-100 transition-colors"
              >
                <span className="material-symbols-outlined text-lg">
                  arrow_back
                </span>
                Back to List
              </button>
            )} */}
            <CancelButton onClick={handleEmbeddedCancel} label="Cancel" />
            <Button variant="primary" onClick={handleSaveUser} disabled={saving}>
              <span className="material-symbols-outlined text-lg">
                {saving ? "hourglass_empty" : "save"}
              </span>
              {saving ? "Saving..." : isEditMode ? "Update User" : "Save User"}
            </Button>
          </div>
        </div>

        {isInactiveEdit && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-5 py-4 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">info</span>
            <span>
              This associate is <strong>Inactive</strong>. All fields are
              locked. Toggle the status to <strong>Active</strong> to enable
              editing.
            </span>
          </div>
        )}

        {saveError && (
          <div className="mb-4 bg-red-50 text-[#ba1a1a] rounded-xl px-5 py-4 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">error</span>
            {saveError}
          </div>
        )}

        {successMsg && (
          <div className="mb-4 bg-[#bbf46e]/20 text-black rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
            <span className="material-symbols-outlined text-[20px] text-black">
              check_circle
            </span>
            {successMsg}
          </div>
        )}

        {/* Form Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-12 lg:col-span-8 space-y-3">
            {/* User Identity Card */}
            <FormCard icon="badge" title="Associate Identity">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Associate Code"
                    name="associateCode"
                    value={formData.associateCode}
                    onChange={handleInputChange}
                    placeholder="Enter associate code"
                    icon="badge"
                    required
                    inputMode="numeric"
                    maxLength={8}
                    error={errors.associateCode}
                    disabled={
                      id &&
                      id !== "create" &&
                      parseInt(id) > 0 &&
                      formData.associateCode &&
                      associateCodeValid &&
                      !associateCodeError
                    }
                  />
                  {isCheckingAssociateCode && (
                    <p className="text-xs text-blue-500 mt-1">
                      Checking availability...
                    </p>
                  )}
                  {associateCodeValid &&
                    formData.associateCode &&
                    !associateCodeError &&
                    !isCheckingAssociateCode && (
                      <p className="text-xs text-green-500 mt-1">
                        ✓ Associate code is available
                      </p>
                    )}
                </div>
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Associate Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                    icon="person"
                    maxLength={ASSOCIATE_NAME_MAX}
                    error={errors.firstName}
                    disabled={isInactiveEdit}
                  />
                  <CharCounter
                    value={formData.firstName}
                    max={ASSOCIATE_NAME_MAX}
                  />
                </div>
              </div>
            </FormCard>

            {/* Security Credentials Card */}
            <FormCard icon="lock" title="Security Credentials">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    required
                    icon="person"
                    maxLength={USERNAME_MAX}
                    error={errors.username}
                    disabled={
                      isInactiveEdit ||
                      (id && id !== "create" && parseInt(id) > 0)
                    }
                  />
                  <CharCounter value={formData.username} max={USERNAME_MAX} />
                  {isCheckingUsername && (
                    <p className="text-xs text-blue-500 mt-1">
                      Checking availability...
                    </p>
                  )}
                  {usernameValid &&
                    formData.username &&
                    !usernameError &&
                    !isCheckingUsername && (
                      <p className="text-xs text-green-500 mt-1">
                        ✓ Username is available
                      </p>
                    )}
                </div>
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={() =>
                      setFormData((prev) => ({
                        ...prev,
                        password: prev.password.trim(),
                      }))
                    }
                    placeholder="Enter password"
                    required={true}
                    icon="key"
                    maxLength={PASSWORD_MAX}
                    error={errors.password}
                    disabled={isInactiveEdit}
                  />
                  <CharCounter value={formData.password} max={PASSWORD_MAX} />
                </div>
              </div>
            </FormCard>

            {/* Contact Details Card */}
            <FormCard icon="contact_mail" title="Contact Details">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={formData.phoneCode}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            phoneCode: e.target.value.replace(/[a-zA-Z]/g, ""),
                          }));
                          if (errors.phoneCode)
                            setErrors((prev) => ({ ...prev, phoneCode: "" }));
                        }}
                        placeholder="Code"
                        maxLength={4}
                        disabled={isInactiveEdit}
                        className={`w-20 px-3 py-3 bg-slate-50 border rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${errors.phoneCode ? "border-red-400" : "border-slate-200"}`}
                      />
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-lg">
                          call
                        </span>
                        <input
                          type="text"
                          value={formData.phoneNumber}
                          onChange={(e) =>
                            handleMobileNumberChange(e.target.value)
                          }
                          placeholder="Enter phone number"
                          maxLength={expectedPhoneLength || 15}
                          disabled={isInactiveEdit}
                          className={`w-full pl-12 pr-4 py-3 bg-slate-50 border rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${errors.phoneNumber ? "border-red-400" : "border-slate-200"}`}
                        />
                        {!errors.phoneCode && errors.phoneNumber && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.phoneNumber}
                          </p>
                        )}
                      </div>
                    </div>
                    {errors.phoneCode && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.phoneCode}
                      </p>
                    )}
                    <CharCounter
                      value={formData.phoneNumber}
                      max={expectedPhoneLength || 15}
                    />
                  </div>
                  {isCheckingMobile && (
                    <p className="text-xs text-blue-500 mt-1">
                      Checking availability...
                    </p>
                  )}
                  {mobileError && (
                    <p className="text-xs text-red-500 mt-1">{mobileError}</p>
                  )}
                  {mobileValid &&
                    formData.phoneNumber &&
                    !mobileError &&
                    !isCheckingMobile && (
                      <p className="text-xs text-green-500 mt-1">
                        ✓ Mobile number is available
                      </p>
                    )}
                  {errors.phoneCode && errors.phoneNumber && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.phoneNumber}
                    </p>
                  )}
                </div>
                <div className="col-span-2 md:col-span-1">
                  <InputField
                    label="Email Identification"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@enterprise-lab.com"
                    required
                    icon="mail"
                    maxLength={EMAIL_MAX}
                    error={errors.email}
                    disabled={isInactiveEdit}
                  />
                  <CharCounter value={formData.email} max={EMAIL_MAX} />
                </div>
              </div>
            </FormCard>
          </div>

          {/* Right Column */}
          <div className="col-span-12 lg:col-span-4 space-y-3">
            {/* Account Access Card */}
            <FormCard icon="lock_person" title="Account Access">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                      Role Selection <span className="text-red-500">*</span>
                    </label>
                    {hasPrivilege(PRIVILEGES.ROLE_CREATE) && (
                      <button
                        type="button"
                        onClick={() => setShowAddRoleModal(true)}
                        disabled={isInactiveEdit}
                        title="Add a new role without leaving this page"
                        className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#08757C]/10 text-[#08757C] text-[11px] font-bold hover:bg-[#08757C]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="material-symbols-outlined text-[14px]">
                          add
                        </span>
                        Add New Role
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
                    {roleOptions.map((role) => (
                      <label
                        key={role.value}
                        className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          value={role.value}
                          checked={formData.roleIds.includes(role.value)}
                          onChange={(e) =>
                            handleRoleChange(role.value, e.target.checked)
                          }
                          disabled={isInactiveEdit}
                          className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-slate-700">
                          {role.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.roleIds && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.roleIds}
                    </p>
                  )}
                </div>

                <ToggleSwitch
                  label="System Status"
                  subLabel="Active Visibility"
                  checked={formData.isActive}
                  onChange={(checked) =>
                    setFormData((prev) => ({ ...prev, isActive: checked }))
                  }
                  activeText="Active"
                  inactiveText="Inactive"
                  disabled={!isEditMode}
                />

                {/* LM is location-scoped — this box only appears once that
                    role is checked above. */}
                {isLmSelected && (
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                      Location Access
                    </label>
                    {cityOptions.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">
                        No active locations found.
                      </p>
                    ) : (
                      <div
                        ref={locationListRef}
                        className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-50"
                      >
                        {cityOptions.map((city) => (
                          <label
                            key={city.value}
                            data-city-id={city.value}
                            className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 p-1 rounded"
                          >
                            <input
                              type="checkbox"
                              value={city.value}
                              checked={formData.cityIds.includes(city.value)}
                              onChange={(e) =>
                                handleCityChange(city.value, e.target.checked)
                              }
                              disabled={isInactiveEdit}
                              className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm text-slate-700">
                              {city.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-[11px] leading-relaxed text-amber-800">
                    <span className="font-bold">Note:</span> Newly created users
                    will receive an automated invitation email to set up their
                    clinical authentication credentials via Enterprise AD.
                  </p>
                </div> */}
              </div>
            </FormCard>

            {/* Visual Helper Card */}
            {/* <div className="relative overflow-hidden rounded-xl h-48 bg-primary p-6 shadow-lg">
              <div className="absolute -right-4 -bottom-4 w-32 h-32 bg-black/10 rounded-full blur-2xl"></div>
              <div className="relative z-10 h-full flex flex-col justify-end">
                <h4 className="text-white font-bold text-sm mb-1">
                  Identity Assurance
                </h4>
                <p className="text-white/70 text-[11px] leading-snug">
                  Ensure Associate Codes match the central HR registry for
                  seamless audit logs.
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Quick add role modal — keeps all typed associate values intact */}
      <QuickAddRoleModal
        isOpen={showAddRoleModal}
        onClose={() => setShowAddRoleModal(false)}
        onCreated={(newId, newName) => {
          // Add the new role to the list and auto-select it
          setRoleOptions((prev) => {
            if (prev.some((r) => String(r.value) === String(newId)))
              return prev;
            return [...prev, { value: newId, label: newName, selected: true }];
          });
          setFormData((prev) => ({
            ...prev,
            roleIds: prev.roleIds.includes(newId)
              ? prev.roleIds
              : [...prev.roleIds, newId],
          }));
          setErrors((prev) => ({ ...prev, roleIds: "" }));
          setSuccessMsg(`Role created successfully.`);
          setTimeout(() => setSuccessMsg(""), 3000);
        }}
      />
    </Wrapper>
  );
};

export default CreateEditUserPage;
